import { 
  Box, 
  Button, 
  Grid, 
  Input, 
  Radio, 
  RadioGroup, 
  SimpleGrid, 
  Stack, 
  Text, 
  Textarea 
} from '@rockr/rock-ui'
import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Autocomplete } from '../../../components/Autocomplete'
import { CustomIcon } from '../../../components/CustomIcon'
import { DateInput } from '../../../components/DateInput'
import { SignersTable } from '../../../components/SignersTable'
import { createGenericTypesService } from '../../../shared/api/generic-types'
import { formatBooleanInput } from '../../../shared/helpers/input'
import { RootState } from '../../../shared/store'
import { saveLegal } from '../../../shared/store/legal/actions'
import { GenericTypes } from '../../../shared/types/generic-types'
import { LegalType } from '../../../shared/types/legal'
import { createAuthProvider } from '../../../shared/providers/auth'
import { OfferInput } from '../../../shared/types/skus'
import { addMonths, differenceInDays, subDays } from 'date-fns'

type Option = {
  value: number,
  label: string
}

type FormattedGenericTypes = {
  id: number
  code: string
  description: string
}

const emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i

const useGenericTypes = () => {
  const [genericTypes, setGenericTypes] = useState<{
    countries: FormattedGenericTypes[],
    geographicSegmentations: FormattedGenericTypes[]
    signerTypes: FormattedGenericTypes[]
    salesQueueTypes: FormattedGenericTypes[]
  }>({
    countries: [],
    geographicSegmentations: [],
    signerTypes: [],
    salesQueueTypes: []
  })

  const formatGenericTypes = (
    genericType: GenericTypes
  ): FormattedGenericTypes => ({
    id: genericType.id,
    code: genericType.code,
    description: genericType.description
  })

  useEffect(() => {
    const genericTypesService = createGenericTypesService()

    Promise.all([
      genericTypesService.getCountries(),
      genericTypesService.getGeographicSegmentations(),
      genericTypesService.getSignerTypes(),
      genericTypesService.getSalesQueueTypes()
    ]).then(([countries, geoSegmentations, signerTypes, salesQueueTypes]) => {
      setGenericTypes({
        countries: countries.map(formatGenericTypes),
        geographicSegmentations: geoSegmentations.map(formatGenericTypes),
        signerTypes: signerTypes.map(formatGenericTypes),
        salesQueueTypes: salesQueueTypes.map(formatGenericTypes)
      })
    })
  }, [])
  return genericTypes
}

export const Legal = () => {
  const authProvider = createAuthProvider()
  const dispatch = useDispatch()

  const {
    values,
    setValues,
    setFieldValue,
    handleChange,
    errors,
    submitCount
  } = useFormikContext<LegalType>()


  const {
    geographicSegmentations,
    countries,
    signerTypes: typeSigners,
    salesQueueTypes: salesQueue
  } = useGenericTypes()


  const [signerEmail, setSignerEmail] = useState<string>()
  const [signatureType, setSignatureType] = useState<number>()
  const [addSignersInvalidFields, setAddSignersInvalidFields] = useState<any>([])

  const legalStore = useSelector<RootState, LegalType>(state => state.legal)
  const offers = useSelector<RootState, OfferInput[]>(state => state.offers)

  const formatGenericTypesToSelect = (
    genericTypes: FormattedGenericTypes[]
  ): Option[] => {
    return genericTypes.map((geographicSegmentation: GenericTypes) => ({
      value: geographicSegmentation.id,
      label: geographicSegmentation.description
    }))
  }

  const findGeographicSegmentation = (description: string) => {
    return geographicSegmentations.find(
      c => c.description === description
    )
  }

  const getSelectedGeographicSegmentation = () => {
    const billingCountryDetails = countries.find(
      c => c.id === Number(values.billingCountryId)
    )
    if (!billingCountryDetails) {
      return null
    }

    const { description } = billingCountryDetails!

    if (description === 'United States' || description === 'Canada') {
      return findGeographicSegmentation('US/Canada')
    }
    return findGeographicSegmentation('International')
  }

  const setGeographicSegmentation = () => {
    if (countries.length > 0) {
      const geographicSegmentation = getSelectedGeographicSegmentation()
      setFieldValue('geographicSegmentationId', geographicSegmentation?.id)
    }
  }

  const handleChangeHasNda = (value: string) => {
    setFieldValue(
      'hasNda',value === 'yes'
    )
  }

  const handleChangeIsAssistedByPartner = (value: string) => {
    setFieldValue('isAssistedByPartner', value === 'yes')
  }

  const displayError = (field: string, message = 'This field is required') => {
    if(Object.keys(errors).includes(field) && submitCount > 0) {
      return <Text fontSize='xs' color='red.500'>{message}</Text>
    }
  }

  const displayErrorAddSigner = (field: string) => {
    if(addSignersInvalidFields && Object.keys(addSignersInvalidFields).includes(field)) {
      return <Text fontSize='xs' color='red.500'>{addSignersInvalidFields[field]}</Text>
    }
  }

  const removeFieldFromAddSignerValidate = (field: string) => {
    let invalidFields = addSignersInvalidFields
    if(invalidFields && Object.keys(invalidFields).includes(field)) {
      delete invalidFields[field]
    }
    setAddSignersInvalidFields(invalidFields)
  }

  const addSignerValidation = () => {
    let invalidFields = {}
    setAddSignersInvalidFields(undefined)
    if(!signerEmail || signerEmail === '') {
      invalidFields = {
        ...invalidFields,
        signerEmail: 'This field is required'
      }
    } else if(!emailRegex.test(signerEmail)) {
      invalidFields = {
        ...invalidFields,
        signerEmail: 'Please, enter a valid email'
      }
    }
    if(!signatureType) {
      invalidFields = {
        ...invalidFields,
        signatureType: 'This field is required'
      }
    }

    if(values.signers.length >= 7) {
      invalidFields = {
        ...invalidFields,
        signerEmail: 'You can add no later than 7 signers'
      }
    }

    if(values.signers.find(signer => signer.email === signerEmail)) {
      invalidFields = {
        ...invalidFields,
        signerEmail: 'This signer was already added'
      }
    }

    if(Object.keys(invalidFields).length > 0) {
      setAddSignersInvalidFields({
        ...addSignersInvalidFields,
        ...invalidFields
      })
      return false
    }
    return true
  }

  const addSigner = () => {
    const isValid = addSignerValidation()
    if(isValid) {
      const signer = {
        email: signerEmail!,
        typeId: signatureType!
      }

      setFieldValue('signers', [
          ...values.signers,
          signer
        ]
      )
    }
  }

  const removeSigner = (index: number) => {
    setFieldValue('signers', [
        ...values.signers.filter((signer, idx) => idx !== index)
      ]
    )
  }

  const formatLegalInitialValues = () => {
    setValues({
      ...legalStore,
      startDate: legalStore.startDate ? new Date(legalStore.startDate) : undefined,
      endDate: legalStore.endDate ? new Date(legalStore.endDate) : undefined
    })
    setGeographicSegmentation()
  }

  const addDefaultWitness = () => {
    const { email } = authProvider.getUser()
    if (typeSigners.length === 0) {
      return
    }

    if(legalStore.signers.find(signer => signer.email === email)) {
      return
    }

    const typeWitness = typeSigners.find(t => t.description === 'Witness')

    if (!typeWitness) {
      return
    }

    setFieldValue('signers',[
        ...legalStore.signers,
        {
          email,
          typeId: typeWitness?.id
        }
      ]
    )
  }
  useEffect(() => {
    setGeographicSegmentation()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.billingCountryId])
  
  useEffect(() => {
    formatLegalInitialValues()
    addDefaultWitness()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeSigners,countries])

  useEffect(() => {
    setSignerEmail('')
    setSignatureType(undefined)
  }, [values.signers])

  useEffect(() => {
    dispatch(saveLegal(values))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  useEffect(() => {
    if (!offers || offers.length < 0 || !values.startDate) {
      return
    }

    const endDate = offers.reduce((endDate, offer) => {
      const newEndDate = addMonths(offer.startDate, offer.duration)
      const diff = differenceInDays(newEndDate, endDate)

      if (diff > 0) {
        return newEndDate
      }

      return endDate
    }, new Date())

    setFieldValue(
      'endDate', subDays(endDate, 1) 
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.startDate])

  return (
    <section>
      <Grid gridGap={4} marginBottom={2}>
        <Text fontSize='md' marginBottom={2}>Add your contract details</Text>
        <SimpleGrid minChildWidth="120px" gridGap={8}>
          <Box>
            <Text fontSize='sm' marginBottom={1}>The contract Has NDA?</Text>
            <RadioGroup
              name="hasNda"
              onChange={handleChangeHasNda}
              value={formatBooleanInput(values.hasNda)}
            >
              <Stack direction="row">
                <Radio value="yes" style={{ borderColor: 'rgb(226, 232, 240)' }}>
                  Yes
                </Radio>
                <Radio value="no" style={{ borderColor: 'rgb(226, 232, 240)' }}>
                  No
                </Radio>
              </Stack>
           </RadioGroup>
           {displayError('hasNda')}
          </Box>
          <Box>
            <Text fontSize='sm' marginBottom={1}>Deal assisted by partner?</Text>
            <RadioGroup
              name="isAssistedByPartner"
              onChange={handleChangeIsAssistedByPartner}
              value={formatBooleanInput(values.isAssistedByPartner)}
            >
              <Stack direction="row">
                <Radio value="yes" style={{ borderColor: 'rgb(226, 232, 240)' }}>
                  Yes
                </Radio>
                <Radio value="no" style={{ borderColor: 'rgb(226, 232, 240)' }}>
                  No
                </Radio>
              </Stack>
            </RadioGroup>
            {displayError('isAssistedByPartner')}
          </Box>


          <Box>
            <Text fontSize='sm' marginBottom={1}>Contract start date</Text>
            <DateInput
              name='startDate'
              dateFormat="dd/MM/yyyy"
              value={values.startDate}
              onChange={handleChange}
            />

            {displayError('startDate')}
          </Box>

          <Box>
            <Text fontSize='sm' marginBottom={1}>Contract end date</Text>
            <DateInput 
              name='endDate' 
              dateFormat="dd/MM/yyyy" 
              value={values.endDate} 
              onChange={handleChange}
              disabled={true}
            />
            {displayError('endDate')}
          </Box>
        </SimpleGrid>
      </Grid>
      <Grid gridGap={4} marginBottom={5}>
        <SimpleGrid minChildWidth="120px" gridGap={6}>
          <Box>
            <Text fontSize='sm' marginBottom={1}>Customers billing country</Text>
            <Autocomplete
              name="billingCountryId"
              options={formatGenericTypesToSelect(countries)}
              defaultValue={undefined}
              onChange={handleChange}
              value={values.billingCountryId}
              isInvalid={Boolean(errors.billingCountryId)}
            />
            {displayError('billingCountryId')}
          </Box>

          <Box>
            <Text fontSize='sm' marginBottom={1}>Geographic Segmentation</Text>
            <Autocomplete
              name="geographicSegmentationId"
              options={formatGenericTypesToSelect(geographicSegmentations)}
              isDisabled={true}
              value={values.geographicSegmentationId}
            />
          </Box>
          <Box>
           {
             values.isNew && (
               <>
                 <Text fontSize='sm' marginBottom={1}>Sales Queue</Text>
                <Autocomplete
                  name="salesQueueId"
                  onChange={handleChange}
                  options={formatGenericTypesToSelect(salesQueue)}
                  value={values.salesQueueId}
                />
               </>
             )  
           }
          </Box>
          <Box></Box>
        </SimpleGrid>
      </Grid>

      {/* Signers */}
      <Grid gridGap={4} marginBottom={5}>
        <Text fontSize='md' marginBottom={2}>Signers</Text>
        <SimpleGrid minChildWidth="120px" gridGap={6}>
          <Box>
            <Text fontSize='sm' marginBottom={1}>Signer email</Text>
            <Input
              placeholder="signer@email.com"
              type='email'
              value={signerEmail}
              onChange={(e) => {
                removeFieldFromAddSignerValidate('signerEmail')
                setSignerEmail(e.target.value)
              }}
            />
            { displayErrorAddSigner('signerEmail') }
          </Box>

          <Box>
            <Text fontSize='sm' marginBottom={1}>Signature type</Text>
            <Autocomplete
              name="signatureType"
              options={formatGenericTypesToSelect(typeSigners)}
              value={signatureType}
              onChange={(e) => {
                removeFieldFromAddSignerValidate('signatureType')
                setSignatureType(e.target.value)
              }}
            />
            { displayErrorAddSigner('signatureType') }
          </Box>

          <Box>
            <Button
              marginTop="1.4rem"
              leftIcon={<CustomIcon name='add' />}
              colorScheme='blue'
              onClick={addSigner}
            >
              Add Signer
            </Button>
          </Box>
          <Box></Box>
        </SimpleGrid>
      </Grid>

      <Grid marginBottom={3}>
        {typeSigners.length > 0 &&
          <SignersTable
            signers={values.signers}
            typeSigners={typeSigners}
            removeSigner={removeSigner}
          />
        }

        <Box marginTop={12}>
          <Text marginBottom={3}>
            Additional info
          </Text>
          <Textarea name="additionalInfo" onChange={handleChange} value={values.additionalInfo}/>
        </Box>
      </Grid>
    </section>
  )
}
